// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The single display source for what Pro costs and what it buys (M10-05, 01 §6).
//
// **One place, because a price written twice is a price that will be wrong in one of them.** Every
// paywall, every upsell, every headset panel and every account screen reads this file. A figure
// typed into a component is a figure nobody updates when the price changes, and the one that gets
// missed is always the one a player screenshots.
//
// **No Price ID here, and a test that keeps it that way.** What a plan *costs to display* and what
// a plan *charges* are deliberately different facts in different places: the ID lives in the Edge
// Function's environment (`server/ts/stripe.ts`), because the moment a browser decides which price
// is charged, an edited bundle decides what a player pays. This file holds figures. It cannot buy
// anything.
//
// **It used to hold the words too, and stopped on 2026-08-24.** A file that says "Cancel any time"
// is a file that has an opinion about which language it is in, and this product now has two. The
// figures, the tiers and the plan shapes stayed; every sentence moved to `src/client/i18n/`, keyed
// by the ids below. The one exception is `Capability.label`, which is kept in English because 01 §6
// is an English document and a test reads that document to check this table transcribes it — the
// French label is an override in the bundle, looked up by capability id, and the English is what it
// falls back to.
//
// **The tier table is data, not markup**, for the same reason 01 §6 is a table rather than three
// paragraphs: the interesting question a visitor has is always comparative — *what do I get that I
// do not have now* — and a list that can be diffed answers it, while three prose blocks do not.

/** The plans, matching `server/ts/stripe.ts`. A test holds the two lists to each other. */
export type PlanId = 'monthly' | 'annual'

/**
 * The currencies this product quotes in, matching `server/ts/stripe.ts`. A test holds them equal.
 *
 * **Two figures deliberately chosen, never one figure converted.** There is no exchange rate in
 * this product and there is not going to be one: a CAD price derived from the USD one by a rate is
 * a number nobody decided, it moves daily, and it stops agreeing with the Stripe Price the moment
 * the rate does. Each currency has its own row, set by a person, and Stripe charges the Price that
 * matches.
 */
export type Currency = 'USD' | 'CAD'

export const CURRENCIES: readonly Currency[] = ['USD', 'CAD']

export const DEFAULT_CURRENCY: Currency = 'USD'

export function isCurrency(value: unknown): value is Currency {
  return typeof value === 'string' && (CURRENCIES as readonly string[]).includes(value)
}

/**
 * Money as an integer count of the currency's minor unit.
 *
 * **Never a float.** `7.99` is not representable in binary64, and the arithmetic this file does —
 * twelve months against one year, a saving as a percentage — is exactly the arithmetic that turns
 * an invisible representation error into a figure that is a cent out on the page. Cents in, and
 * formatting is the only thing that ever divides.
 */
export interface Money {
  readonly amountMinor: number
  /** ISO 4217, upper case. */
  readonly currency: string
}

export interface PricingPlan {
  readonly id: PlanId
  readonly price: Money
  /** The unit the price is quoted per, for the "/month" after the number. */
  readonly per: 'month' | 'year'
  /**
   * Which line goes under the price, as a key rather than a sentence. Null where there is nothing
   * honest to add.
   *
   * **A key, because this file stopped being able to hold copy the day it had to hold French.**
   * The words live in `src/client/i18n/`, keyed by this; what stays here is the figure and the fact
   * that there *is* a line. A plan's own name is derived from `id` the same way.
   */
  readonly noteKey: 'cancel-any-time' | 'one-month-free' | null
}

/**
 * Launch pricing, from 01 §6.
 *
 * **Confirmed with the product owner on 2026-08-23**, at the point Checkout, the Portal and the
 * webhook were built — 01 §6 carried a verification marker until then and no longer does.
 *
 * **Revised 2026-08-24: the annual plan is one month free**, which is eleven months' money rounded
 * up to the next whole dollar. It was $49.99 against $95.88, a 47% discount that was never
 * intended — the note beside it said "two months free" and meant it, and the two had disagreed
 * since the figures were first written down. One month is the decision; the arithmetic below is
 * what it produces, and the note now says what the number does.
 *
 *   USD  11 x 7.99 =  87.89  ->  $88.00
 *   CAD  11 x 9.99 = 109.89  -> $110.00
 *
 * **The CAD figures are set, not converted.** $9.99 is a deliberate price point rather than USD
 * $7.99 at the day's rate (which would be about $10.95): it is the conventional Canadian anchor and
 * it reads materially cheaper than a double-digit figure. What it costs is roughly nine percent of
 * margin per Canadian subscriber against parity, and that is the trade — market share on entry.
 *
 * Changing any of them is a change to the requirements document as well as to this file, and to
 * the Stripe Price that charges it.
 */
export const PRICING: Readonly<Record<Currency, readonly PricingPlan[]>> = {
  USD: [
    { id: 'monthly', price: { amountMinor: 799, currency: 'USD' }, per: 'month', noteKey: 'cancel-any-time' },
    { id: 'annual', price: { amountMinor: 8800, currency: 'USD' }, per: 'year', noteKey: 'one-month-free' },
  ],
  CAD: [
    { id: 'monthly', price: { amountMinor: 999, currency: 'CAD' }, per: 'month', noteKey: 'cancel-any-time' },
    { id: 'annual', price: { amountMinor: 11000, currency: 'CAD' }, per: 'year', noteKey: 'one-month-free' },
  ],
}

/** Every plan in one currency, in display order. */
export function plansIn(currency: Currency): readonly PricingPlan[] {
  return PRICING[currency]
}

export function planById(id: PlanId, currency: Currency = DEFAULT_CURRENCY): PricingPlan {
  const found = PRICING[currency].find((plan) => plan.id === id)
  // Unreachable through the type, and thrown rather than defaulted: a paywall that silently showed
  // the monthly price for a plan it did not recognise would charge the annual one.
  if (found === undefined) throw new Error('No such plan: ' + id + ' in ' + currency)
  return found
}

/**
 * A price as a string, in the currency it is quoted in.
 *
 * `Intl.NumberFormat` rather than a `$` and a `toFixed(2)`, because the symbol, its side of the
 * number and the separators are all decided by the reader's locale and none of them is a constant.
 * That matters more with two currencies and two languages than it did with one of each: the same
 * $110.00 is `CA$110.00` to an American reader and `110,00 $ CA` to a French-Canadian one, and
 * neither of those is a string this product should be assembling by hand.
 */
export function formatMoney(money: Money, locale?: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: money.currency,
  }).format(money.amountMinor / 100)
}

/**
 * What the annual plan costs per month, for the comparison every subscription page makes.
 *
 * Rounded to the nearest cent for display and **never used to charge anything** — the annual plan
 * charges the annual price, and this is the number beside it that makes the saving legible.
 */
export function monthlyEquivalent(plan: PricingPlan): Money {
  if (plan.per === 'month') return plan.price
  return { amountMinor: Math.round(plan.price.amountMinor / 12), currency: plan.price.currency }
}

/**
 * How much less a year of the annual plan is than twelve months of the monthly one, as a
 * percentage, rounded down.
 *
 * **Rounded down, deliberately.** A saving rounded up is a claim that is a fraction of a percent
 * generous, and a subscription page is the last place to be even slightly generous about a number.
 * Null when the two plans are not comparable, rather than a zero that reads as "no saving".
 */
export function annualSavingPercent(currency: Currency = DEFAULT_CURRENCY): number | null {
  const monthly = planById('monthly', currency)
  const annual = planById('annual', currency)
  if (monthly.price.currency !== annual.price.currency) return null
  const twelve = monthly.price.amountMinor * 12
  if (twelve <= 0 || annual.price.amountMinor >= twelve) return null
  return Math.floor(((twelve - annual.price.amountMinor) / twelve) * 100)
}

// ---------------------------------------------------------------------------
// What each tier buys — 01 §6, as data
// ---------------------------------------------------------------------------

export type Tier = 'anonymous' | 'free' | 'pro'

export interface Capability {
  /** Stable id, so a paywall can point at the row that sent the player there (M10-16). */
  readonly id: string
  readonly label: string
  /** The tiers that have it. Anything not listed does not. */
  readonly tiers: readonly Tier[]
}

/**
 * The tier table, in 01 §6's own order.
 *
 * The order is the argument: it runs from what anybody can do to what only a subscriber can, so
 * reading down it is the same experience as running out of free tier.
 */
export const CAPABILITIES: readonly Capability[] = [
  { id: 'play-web', label: 'NEON NOVA in the browser, desktop or phone', tiers: ['anonymous', 'free', 'pro'] },
  { id: 'local-scores', label: 'Local high scores in that browser', tiers: ['anonymous', 'free', 'pro'] },
  { id: 'cloud-scores', label: 'Cloud score sync, game history, replays', tiers: ['free', 'pro'] },
  { id: 'leaderboards', label: 'Global and friends leaderboards', tiers: ['free', 'pro'] },
  { id: 'hotseat', label: '1–4 player alternating-ball games, same device', tiers: ['anonymous', 'free', 'pro'] },
  { id: 'online-games', label: 'Online alternating-ball games with friends', tiers: ['free', 'pro'] },
  { id: 'tournaments', label: 'Weekly tournaments', tiers: ['free', 'pro'] },
  { id: 'community-machines', label: 'Play machines published by other players', tiers: ['pro'] },
  { id: 'builder-private', label: 'Machine Builder — design and play privately', tiers: ['pro'] },
  { id: 'builder-publish', label: 'Machine Builder — publish publicly', tiers: ['pro'] },
  { id: 'builder-api', label: 'Machine Builder — build with your own AI, and the Builder API', tiers: ['pro'] },
  { id: 'headset', label: 'Headset VR and MR (WebXR and native)', tiers: ['pro'] },
  { id: 'arcade', label: 'The shared virtual arcade, avatars and spatial voice', tiers: ['pro'] },
  { id: 'full-roster', label: 'Full machine roster beyond NEON NOVA', tiers: ['pro'] },
]

export type GateVerdict = 'allowed' | 'checking' | 'needs-account' | 'needs-pro'

/**
 * Whether this viewer may do this thing (M10-12 to M10-16).
 *
 * **Here rather than beside the account code, because the front door needs it.** The headset entry
 * lives on the page a visitor plays with no account, and FR-030 forbids that page from reaching
 * anything that could construct a Supabase client — so the question "may this tier do this" has to
 * live somewhere with no dependencies at all. It lives with the table it is asking about, which is
 * where it belonged anyway.
 *
 * **Three verdicts, not two.** *Needs an account* and *needs Pro* are a free door and a paid one,
 * and a visitor shown a price for something that costs nothing has been lied to. `checking` is its
 * own answer for the same reason: showing a paywall for the half-second before `/entitlement`
 * replies advertises Pro to somebody who already pays for it, on every page load.
 *
 * An unknown capability id is **allowed**: a gate for something this table does not describe is a
 * gate nobody wrote a reason for, and refusing on it would turn a typo into a locked door.
 */
export function gateVerdict(capability: string, tier: Tier, loading: boolean): GateVerdict {
  if (loading) return 'checking'
  const row = CAPABILITIES.find((entry) => entry.id === capability)
  if (row === undefined) return 'allowed'
  if (row.tiers.includes(tier)) return 'allowed'
  // Anonymous, and short of something a *free account* would give. An account is the honest next
  // step and it costs nothing.
  if (tier === 'anonymous' && row.tiers.includes('free')) return 'needs-account'
  return 'needs-pro'
}

/**
 * What Pro adds to a tier — the only list a paywall should ever show.
 *
 * **A comparison, not a feature list.** Showing a signed-in player "cloud score sync" as a reason to
 * subscribe, when they already have it, is the fastest way to make a paywall read as dishonest.
 */
export function proAddsFor(tier: Tier): readonly Capability[] {
  return CAPABILITIES.filter(
    (capability) => capability.tiers.includes('pro') && !capability.tiers.includes(tier),
  )
}

/**
 * What a subscription actually buys — always measured against a **free account**, never against
 * nothing.
 *
 * **This is the list that may appear above a price, and `proAddsFor` is not.** A visitor shown
 * `proAddsFor('anonymous')` beside $7.99 is being told that leaderboards, tournaments and the
 * Builder are things to pay for. They are not: they come with an account that costs nothing, and
 * listing them over a Subscribe button is precisely the dishonest paywall this file exists to
 * prevent. The browser caught it — ten items over two prices, six of them free.
 */
export function paidAdds(): readonly Capability[] {
  return proAddsFor('free')
}

/**
 * What signing up gets somebody who has not, minus anything that costs money.
 *
 * The second, cheaper offer a visitor deserves to see beside the first — and very often the one
 * they actually wanted. Empty for anybody who already has an account.
 */
export function freeAccountAdds(tier: Tier): readonly Capability[] {
  if (tier !== 'anonymous') return []
  const paid = paidAdds()
  return proAddsFor('anonymous').filter(
    (capability) => !paid.some((member) => member.id === capability.id),
  )
}
